import Commons from "oc-commons/Commons";
import Scenes from "oc-threejs/Scenes";
import UniversalLoader from "oc-threejs/loader/UniversalLoader";
import Jaco from "../arrangement/Jaco";
import Urls from "oc-commons/Urls";
import ReplacingMaterialFactory from "oc-threejs/loader/ReplacingMaterialFactory";
import Models from "oc-app/Models";

/**
 * @template {{new (...args: any[]) => {}} Super
 * @param {Super} superClass
 */
const Jaco3dMixin = Commons.cache((superClass = THREE.Group) => class extends superClass {

    // Mittels Jaco3D.Mixin(BasisKlasse) kann man Jaco3d als Mixin benutzen, d.h. von einer anderen Klasse als Group erben lassen
    // das ist praktisch, wenn man z.B. statt dessen von THREE.Line ableiten möche
    static Mixin = Jaco3dMixin;

    /** @type {Jaco} */
    jaco;
    forbidAutoRemove = false;
    _updateId = -1;
    _jacoOld = {};
    _jacoChanged = {};

    /**
     *
     * @param {Jaco} jaco
     */
    constructor(jaco) {
        super();
        this.jaco = jaco;
        this.name = jaco?.getJaco3dName();
    }

    /**
     * Sinn dieser Methode ist es, in den Typen die Materialien, Geometrien etc. nur bei Bedarf zu ändern.
     * Gibt zurück, ob ein Wert aus JacoObj sich geändert hat im Vergleich zum letztmaligen update3d-Aufruf
     * @param {string} path
     * @param {*} [accessor=jaco => jaco ? jaco[path] : undefined]
     * @returns {boolean}
     */
    changed(path, accessor = jaco => jaco ? jaco[path] : undefined) {
        const buffered = this._jacoChanged[path];
        if (buffered !== undefined) {
            return buffered;
        }
        const valueNew = accessor(this.jaco);
        const valueOld = this._jacoOld[path];
        const changed = this._jacoChanged[path] = !Commons.isEqualEpsilon(valueOld, valueNew);
        if (changed) {
            this._jacoOld[path] = Commons.cloneDeep(valueNew);
        }
        return changed;
    }

    /**
     * Diese Methode muss im Rahmen des jaco-update3d aufgerufen werden. Objekte, die nicht
     * touched sind, und nicht forbidAutoRemove=false haben, werden abgeräumt
     */
    setTouchedByJaco() {
        this._updateId = APP._currentUpdateId;
        this._jacoChanged = {};
    }

    /**
     * Kopiert Standardmäßig Drehung und Position des Grundobjekts, und Opacity/visibility
     * Überschreiben falls das nicht gewünscht ist.
     */
    update3d() {
        this.copyCoordinateSystem(this.jaco);
        this.copyOpacity(this.jaco);
    }

    /** */
    copyOpacity(jaco) {
        this.visible = jaco.visible;
        this.opacity = jaco.opacity;
    }

    /** */
    copyCoordinateSystem(target) {
        this.position.copy(target.position);
        this.quaternion.copy(target.quaternion);
    }

    /**
     * @param {String} key
     * @param {(THREE.Group)=>void} onLoadCallback
     * @returns {THREE.Group}
     */
    loadObject3DByModelKey(key, onLoadCallback) {
        const model = APP.richMetadata.findModel(key);
        if (model == null) {
            throw new Error('Model with key ' + key + ' not found');
        }
        return this.loadObject3DByOptions({...model,onLoadCallback});
    }

    /**
     * @param {Object} options
     * @returns {THREE.Group}
     */
    loadObject3DByOptions(options) {

        let group = new THREE.Group();

        // dummyObjekt - Workaround: Laden ist asynchron. Wenn das Objekt leer ist - also gar keine Geometrie
        // enthält - dann klappt drag&drop nicht, das Objekt kann nicht in die Szene gedraggt werden (bounding box?)
        let loadingDummy = null;

        if (this.draggable || STAGE.settings.alwaysShowLoadingDummy) {
            // TODO evtl. loadingDummy cachen?
            loadingDummy = this.createLoadingDummy(options);
            if (loadingDummy) {
                group.add(loadingDummy);
            }
            STAGE.dirty();
        }

        Commons.delayUntil(() => {
            const cacheKey = Commons.cacheKey(options.Path, this.materialMap, this.jaco.materialMap, options.MaterialMap);
            let promise = UniversalLoader.loadModelPromise(this.getModelUrls(options), this.getMaterialFactory(options), cacheKey);
            promise = UniversalLoader.getPromiseAllImagesLoaded(promise);
            promise.then(object => {
                if (object==null) {
                    return;
                }
                Scenes.remove(loadingDummy);
                this.transformObject3dAfterLoad(object, options);
                group.add(object);
                if (options.onLoadCallback) {
                    options.onLoadCallback(group);
                }
                STAGE.dirty();
            }, error => {
                //404 werden bereits vom UniversalLoader auf console ausgegeben
            });

        }, () => UniversalLoader.getMaterialFactory() != null);

        return group;
    }

    /**
     * @returns {string[]}
     */
    getModelUrls(options) {
        if (options.Path==null) {
            return [];
        }
        let urls = options.Path.split(',');
        const [hasOBJ, hasMTL] = ['.obj', '.mtl'].map(suffix => urls.some(s => s.endsWith(suffix)));
        if (hasMTL && !hasOBJ) {
            const obj = urls[0].replace(/.mtl$/i, '.obj');
            urls.push(obj);
        }
        const absoluteUrls = urls.map(url => Urls.isAbsolute(url) ? url : APP.settings.wkfgContentBaseUrl + url);
        return absoluteUrls;
    }

    /**
     * @returns {ReplacingMaterialFactory|ReplacingMaterialFactory}
     */
    getMaterialFactory(options) {
        return new ReplacingMaterialFactory([this.materialMap, this.jaco.materialMap, options?.MaterialMap], UniversalLoader.getMaterialFactory());
    }

    /**
     * @param {THREE.Object3D} object
     */
    transformObject3dAfterLoad(object, options) {
        Models.applyToObject3d(options,object);
        return object;
    }

    /**
     * @returns {THREE.Object3D}
     */
    createLoadingDummy(options) {
        const boundingBox = options.ModelData?.boundingBox;
        const box = Scenes.box(boundingBox.getSize(), {color: 0xa0a0a0, transparent: true, opacity: 0.5});
        box.position.copy(boundingBox.getCenter());
        return box;
    }

});

const Jaco3d = Jaco3dMixin();
export default Jaco3d;
